import React, { useState, useEffect, useContext } from "react";
import { withOktaAuth } from "@okta/okta-react";
import { withRouter } from "react-router-dom";
import {
  withStyles,
  Grid,
  Dialog,
  Typography,
  IconButton,
  RadioGroup,
  Radio,
  FormControlLabel,
} from "@material-ui/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import CloseIcon from "@material-ui/icons/Close";
import InputLabel from "@material-ui/core/InputLabel";
import LoadingIndicator from "../../../../Component/LoadingIndicator";
import ModalDialogContext from "../../../../Context/ModalDialog/ModalDialogContext";
import { getListValues } from "../../../../API/List";
import { getGeographies } from "../../../../API/Country";
import SearchableSelect from "../../../../Component/Forms/SearchableSelect";
import TextField from "../../../../Component/Forms/TextField";
import { recordTypes } from "../../../../API/config";
import styles from "./styles";
import { useTranslation } from "react-i18next";
import CustomButton from "../../../../Component/CustomButton";

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

const CreateEntity = (props) => {
  const {
    classes,
    open,
    user,
    hideCheckTypeSelection = false,
    disableCheckTypeSelection = false,
    defaultCheckType = "KYC",
    selectedRecord,
    onSubmit,
    onClose,
    hasPermission,
  } = props;
  const { t } = useTranslation();

  const [openCreateDialog, setOpenCreateDialog] = useState(open);
  const [countryList, setCountryList] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [jllCountry, setJllCountry] = useState(null);
  const [addressLine1, setAddressLine1] = useState();
  const [addressLine2, setAddressLine2] = useState();
  const [addressLine3, setAddressLine3] = useState();
  const [addressLine4, setAddressLine4] = useState();
  const [recordName, setRecordName] = useState("");
  const [region, setRegion] = useState(null);
  const [regionList, setRegionList] = useState([]);
  const [city, setCity] = useState();
  const [county, setCounty] = useState();
  const [stateProvince, setStateProvince] = useState();
  const [postCode, setPostCode] = useState();
  const [checkType, setCheckType] = useState(defaultCheckType);
  const [addressErrorMessage, setAddressErrorMessage] = useState();
  const [townErrorMessage, setTownErrorMessage] = useState();
  const [postCodeErrorMessage, setPostCodeErrorMessage] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [recordErrorMessage, setRecordErrorMessage] = useState();
  const [checkTypeErrorMessage, setCheckTypeErrorMessage] = useState();
  const [jllCountryErrorMessage, setJllCountryErrorMessage] = useState();
  const [countryErrorMessage, setCountryErrorMessage] = useState();
  const [entityCategories, setEntityCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryError, setCategoryError] = useState();
  const [canCreateKyc, setCanCreateKyc] = useState();
  const [canCreateAml, setCanCreateAml] = useState();

  const { showErrorMessage } = useContext(ModalDialogContext);

  useEffect(() => {
    const evalPermissions = async () => {
      const canCreateKyc = await hasPermission(
        recordTypes.checkRequest.name,
        recordTypes.checkRequest.actions.createKycCheck.name
      );
      const canCreateAml = await hasPermission(
        recordTypes.checkRequest.name,
        recordTypes.checkRequest.actions.createAmlCheck.name
      );
      setCanCreateKyc(canCreateKyc);
      setCanCreateAml(canCreateAml);
    };

    const loadCountries = async () => {
      setIsLoading(true);
      try {
        const resuts = await getListValues({ listType: "CL" });
        const { data = [] } = resuts;
        setCountryList(
          data.map((c) => {
            return { value: c.code, label: c.value, region: c.region };
          })
        );

        const userCountry = data.find((c) => c.isoCode2 === user.locale);
        if (userCountry) {
          setJllCountry({
            value: userCountry.code,
            label: userCountry.value,
          });
        }

        const geographyData = await getGeographies();
        setRegionList(
          geographyData.data.map((g) => {
            return { value: g.name, label: g.name };
          })
        );
      } catch (error) {
        showErrorMessage({ error });
        setIsLoading(false);
      }
      setIsLoading(false);
    };

    const loadEntityCategories = async () => {
      const results = await getListValues({ listName: "Legal Status" });
      const { data = [] } = results;
      setEntityCategories(
        data.map((lv) => {
          return { value: lv.code, label: lv.value };
        })
      );
    };
    setOpenCreateDialog(open);
    if (openCreateDialog || open) {
      evalPermissions();
      loadCountries();
      loadEntityCategories();
    }
  }, [open]);

  useEffect(() => {
    const loadEntityBlueprint = () => {
      setRecordName(selectedRecord.recordName);
      setSelectedCategory(entityCategories.filter(e => e.value === selectedRecord.selectedCategory)[0]);
      const { address } = selectedRecord;
      if (address) {
        setAddressLine1(address.address_line_1);
        setAddressLine2(address.address_line_2);
        setCity(address.city);
        setCounty(address.county);
        setPostCode(address.postcode);
      }
    }
    if (selectedRecord) {
      loadEntityBlueprint();
    }
  }, [entityCategories, selectedRecord]);

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          // height: "70vh",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  const checkTypeChanged = (e) => {
    setCheckType(e.target.value);
  };

  const renderCreateForm = () => {
    return (
      <Grid container direction="row" spacing={3}>
        <Grid item sm={6} md={9} xs={12}>
          <TextField
            label={t("labels.record.createForm.fullName")}
            name="input_name"
            type="text"
            id="input_name"
            placeholder={t("labels.record.createForm.enterName")}
            value={recordName}
            initialValue={recordName}
            onChange={setRecordName}
            required={true}
          />

          <div className={classes.errorText}>{recordErrorMessage}</div>
        </Grid>
        <Grid item sm={6} md={3} xs={12}>
          <SearchableSelect
            label={t("labels.record.createForm.legalStatus")}
            options={entityCategories}
            value={selectedCategory}
            changeHandler={setSelectedCategory}
            disableClearable={true}
            required={true}
          />
          <div className={classes.errorText}>{categoryError}</div>
        </Grid>

        {!hideCheckTypeSelection && (
          <Grid item sm={6} md={6} xs={12}>
            <InputLabel className={classes.inputLabel}>
              {t("labels.record.createForm.checkType")}
              <span
                aria-hidden="true"
                className="MuiFormLabel-asterisk MuiInputLabel-asterisk"
                style={{ color: "#E30613" }}
              >
                &thinsp;*
              </span>
            </InputLabel>
            <RadioGroup
              aria-label="checkType"
              name="checkType"
              value={checkType}
              onChange={checkTypeChanged}
              row
              size="small"
              margin="dense"
            >
              {canCreateKyc && (
                <FormControlLabel
                  value="KYC"
                  control={<Radio color="primary" />}
                  label={t("labels.checks.KYC")}
                  labelPlacement="end"
                  disabled={disableCheckTypeSelection}
                />
              )}
              {canCreateAml && (
                <FormControlLabel
                  value="AML"
                  control={<Radio color="primary" />}
                  label={t("labels.checks.amlKyc")}
                  labelPlacement="end"
                  disabled={disableCheckTypeSelection}
                />
              )}
            </RadioGroup>
            <div className={classes.errorText}>{checkTypeErrorMessage}</div>
          </Grid>
        )}
        {!hideCheckTypeSelection && (
          <Grid item sm={6} md={6} xs={12}>
            <SearchableSelect
              label={t("labels.record.createForm.jllCountry")}
              options={countryList}
              value={jllCountry}
              changeHandler={setJllCountry}
              required={true}
            />
            <div className={classes.errorText}>{jllCountryErrorMessage}</div>
          </Grid>
        )}

        <Grid item sm={6} md={6} xs={12}>
          <TextField
            label={t("labels.record.createForm.addressLine1")}
            name="input_add1"
            type="text"
            id="input_add1"
            placeholder={t("labels.record.createForm.enterAddressLine1")}
            value={addressLine1}
            initialValue={addressLine1}
            onChange={setAddressLine1}
            required={true}
          />
          <div className={classes.errorText}>{addressErrorMessage}</div>
        </Grid>
        <Grid item sm={6} md={6} xs={12}>
          <TextField
            label={t("labels.record.createForm.addressLine2")}
            name="input_add2"
            type="text"
            id="input_add2"
            fullWidth
            placeholder={t("labels.record.createForm.enterAddressLine2")}
            value={addressLine2}
            initialValue={addressLine2}
            onChange={setAddressLine2}
          />
        </Grid>
        <Grid item sm={6} md={6} xs={12}>
          <TextField
            label={t("labels.record.createForm.addressLine3")}
            name="input_add3"
            type="text"
            id="input_add3"
            fullWidth
            placeholder={t("labels.record.createForm.enterAddressLine3")}
            value={addressLine3}
            onChange={setAddressLine3}
          />
        </Grid>
        <Grid item sm={6} md={6} xs={12}>
          <TextField
            label={t("labels.record.createForm.addressLine4")}
            name="input_add4"
            type="text"
            id="input_add4"
            placeholder={t("labels.record.createForm.enterAddressLine4")}
            value={addressLine4}
            onChange={setAddressLine4}
          />
        </Grid>

        <Grid item sm={6} md={3} xs={12}>
          <TextField
            label={t("labels.record.createForm.city")}
            name="input_city"
            type="text"
            id="input_city"
            placeholder={t("labels.record.createForm.enterTownOrCity")}
            value={city}
            initialValue={city}
            onChange={setCity}
            required={true}
          />
          <div className={classes.errorText}>{townErrorMessage}</div>
        </Grid>
        <Grid item sm={6} md={3} xs={12}>
          <TextField
            label={t("labels.record.createForm.county")}
            name="input_county"
            type="text"
            id="input_county"
            placeholder={t("labels.record.createForm.enterCounty")}
            value={county}
            initialValue={county}
            onChange={setCounty}
          />
        </Grid>
        <Grid item sm={6} md={3} xs={12}>
          <TextField
            label={t("labels.record.createForm.stateOrProvince")}
            name="input_stateProvince"
            type="text"
            id="input_stateProvince"
            placeholder={t("labels.record.createForm.enterStateProvince")}
            value={stateProvince}
            onChange={setStateProvince}
          />
        </Grid>
        <Grid item sm={6} md={3} xs={12}>
          <SearchableSelect
            label={t("labels.record.createForm.country")}
            options={countryList}
            value={selectedCountry}
            changeHandler={(e) => {
              if (e) {
                setRegion({ value: e.region, label: e.region });
              }
              setSelectedCountry(e);
            }}
            required={true}
          />
          <div className={classes.errorText}>{countryErrorMessage}</div>
        </Grid>
        <Grid item sm={6} md={3} xs={12}>
          <SearchableSelect
            label={t("labels.record.addresses.form.region")}
            options={regionList}
            value={region}
            changeHandler={setRegion}
          />
        </Grid>
        <Grid item sm={6} md={3} xs={12}>
          <TextField
            label={t("labels.record.createForm.postCode")}
            name="input_postCode"
            type="text"
            id="input_postCode"
            placeholder={t("labels.record.createForm.enterPostCode")}
            value={postCode}
            initialValue={postCode}
            onChange={setPostCode}
            required={false}
          />
          <div className={classes.errorText}>{postCodeErrorMessage}</div>
        </Grid>
      </Grid>
    );
  };

  const closeCreateDialog = () => {
    setOpenCreateDialog(false);
    onClose();
  };

  const submitEntity = async () => {
    if (!recordName || recordName === "") {
      setRecordErrorMessage(t("messages.record.createForm.recordNameError"));
      return;
    }

    if (!selectedCategory) {
      setCategoryError(t("messages.record.createForm.recordTypeError"));
      return;
    }

    if (!hideCheckTypeSelection) {
      if (!checkType) {
        setCheckTypeErrorMessage(t("messages.record.createForm.checkTypeError"));
        return;
      }
      if (!jllCountry) {
        setJllCountryErrorMessage(t("messages.record.createForm.countryError"));
        return;
      }
    }

    if (!addressLine1 || addressLine1 === "") {
      setAddressErrorMessage(t("messages.record.createForm.addressLine1Error"));
      return;
    } else if (addressLine1.length > 100) {
      setAddressErrorMessage(t("messages.record.createForm.addressLine1LengthError"));
      return;
    }

    if (!city || city === "") {
      setTownErrorMessage(t("messages.record.createForm.cityError"));
      return;
    } else if (city.length > 50) {
      setTownErrorMessage(t("messages.record.createForm.cityLengthError"));
      return;
    }

    if (postCode && postCode.length > 10) {
      setPostCodeErrorMessage(t("messages.record.createForm.postCodeLengthError"));
      return;
    }

    if (!selectedCountry) {
      setCountryErrorMessage(t("messages.record.createForm.countryError"));
      return;
    }

    setIsLoading(true);
    setIsCreating(true);
    await onSubmit({
      entityName: recordName,
      entityCategory: selectedCategory.value,
      addressLine1,
      addressLine2,
      addressLine3,
      addressLine4,
      city,
      county,
      stateProvince,
      region: region ? region.value : null,
      postCode,
      countryId: selectedCountry.value,
      jllCountryId: jllCountry.value,
      checkType,
    });
    setOpenCreateDialog(false);
    onClose();
    setIsLoading(false);
    setIsCreating(false);
  };

  return (
    <>
      <Dialog
        disableBackdropClick
        onClose={closeCreateDialog}
        aria-labelledby="customized-dialog-title"
        open={openCreateDialog}
        fullWidth={true}
        scroll="paper"
        maxWidth="md"
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle id="customized-dialog-title" onClose={closeCreateDialog}>
          <Typography className={classes.panelTitle}>
            <span>{t("labels.record.createForm.header")} {isLoading}</span>
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {isLoading ? renderLoadingIndicator() : renderCreateForm()}
        </DialogContent>
        <DialogActions>
          <CustomButton 
            variant="secondary" 
            isDisabled={isLoading}
            onClick={closeCreateDialog}
          >
            {t("labels.common.cancel")}
          </CustomButton>
          <CustomButton 
            variant="primary" 
            loadingText={isCreating ? t("labels.record.createForm.creating") : t("labels.common.submit")}
            isLoading={isLoading}
            isDisabled={isLoading}
            onClick={submitEntity}
          >
            {t("labels.common.submit")}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default withRouter(
  withOktaAuth(withStyles(styles, { withTheme: true })(CreateEntity))
);
